import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { FiBarChart2, FiCalendar, FiChevronRight, FiDownload, FiInfo, FiMoreHorizontal, FiPhoneCall, FiRefreshCw, FiSearch, FiStar, FiTag, FiUpload, FiUsers } from "react-icons/fi";
import { IoChevronDownOutline } from "react-icons/io5";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { RiGitBranchLine } from "react-icons/ri";
import { TbListCheck } from "react-icons/tb";
import useCallCenterT from "../../hooks/useCallCenterT";
import { getDashboardAnalytics } from "../../service/CallCenterAnalytics";
import AgentAlertsPanel from "../../components/call-center/AgentAlertsPanel";
import { ORG_CONTEXT_CHANGED_EVENT } from "../../utils/organizationContext";
import { hasScreenAccess } from "../../utils/permissions";
import { isCallingEnabled } from "../../utils/orgFeatures";
import { ImStatsBars } from "react-icons/im";
import { TbColumns3 } from "react-icons/tb";

const DASHBOARD_DEFAULT_TIMEZONE = "Asia/Muscat";

const formatInAnalyticsTimezone = (value, timezone = DASHBOARD_DEFAULT_TIMEZONE) => {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

const percent = (value, total) => {
  const num = Number(value || 0);
  const den = Number(total || 0);
  if (!den) return 0;
  return Math.max(0, Math.min(100, Math.round((num / den) * 100)));
};

const pickNumber = (value) => {
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const getPreviousValue = (source, key) => {
  if (!source) return null;
  const capitalized = key.charAt(0).toUpperCase() + key.slice(1);
  const candidates = [
    `previous${capitalized}`,
    `${key}Previous`,
    `${key}LastPeriod`,
    `lastPeriod${capitalized}`,
  ];
  for (const candidate of candidates) {
    if (source[candidate] != null) return pickNumber(source[candidate]);
  }
  return null;
};

const formatCount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isNaN(num)) return num.toLocaleString();
  return String(value);
};

const formatRecoveryDisplay = (value) => {
  if (value == null || value === "") return "—";
  const raw = String(value).trim();
  if (raw.toUpperCase().includes("OMR")) return raw;
  const num = Number(raw.replace(/[^\d.-]/g, ""));
  if (Number.isNaN(num)) return raw;
  if (Math.abs(num) >= 1000) {
    const compact = (num / 1000).toFixed(1).replace(/\.0$/, "");
    return `OMR ${compact}K`;
  }
  return `OMR ${num.toLocaleString()}`;
};

const formatComparisonValue = (value, isRecovery = false) => {
  if (value == null) return "";
  return isRecovery ? formatRecoveryDisplay(value) : formatCount(value);
};

const computeAgentRating = (member, maxRecovery) => {
  const assigned = Number(member?.assignedTargets || 0);
  const called = Number(member?.calledTargets || 0);
  const paid = Number(member?.paidTargets || 0);
  const recovery = Number(member?.recoveryAmount || 0);

  if (called === 0 && assigned === 0 && recovery === 0) {
    return 0;
  }

  const coverageScore = assigned > 0 ? Math.min(1, called / assigned) : called > 0 ? 1 : 0;
  const conversionScore = called > 0 ? Math.min(1, paid / called) : 0;
  const recoveryScore = maxRecovery > 0 ? Math.min(1, recovery / maxRecovery) : 0;
  const composite = coverageScore * 0.35 + conversionScore * 0.35 + recoveryScore * 0.3;
  return Math.round(composite * 50) / 10;
};

const calcTrendPercent = (current, previous) => {
  const cur = pickNumber(current);
  const prev = pickNumber(previous);
  if (cur == null || prev == null || prev === 0) return null;
  return Math.round(((cur - prev) / prev) * 1000) / 10;
};

const buildMetricMeta = (current, previous, isRecovery = false) => {
  const trend = calcTrendPercent(current, previous);
  const comparison =
    previous != null
      ? `vs. ${formatComparisonValue(previous, isRecovery)} last period`
      : null;
  return { trend, comparison };
};
const RingProgress = ({ value, size = 132, stroke = 12, className = "text-[#2B7FFF]" }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const half = c / 2;
  const offset = half - (Math.max(0, Math.min(100, value)) / 100) * half;
  return (
    <svg width={size} height={size / 2 + 8} viewBox={`0 0 ${size} ${size / 2 + 8}`} className={className} aria-hidden>
      <path
        d={`M ${stroke / 2} ${size / 2} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${size / 2}`}
        fill="none"
        className="stroke-slate-100"
        strokeWidth={stroke}
        strokeLinecap="round"
      />
      <path
        d={`M ${stroke / 2} ${size / 2} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${size / 2}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={half}
        strokeDashoffset={offset}
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
};

const toPoints = (values, width, height, padX = 16, padY = 18) => {
  if (!values.length) return "";
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  return values
    .map((value, index) => {
      const x = padX + (index / Math.max(values.length - 1, 1)) * (width - padX * 2);
      const y = height - padY - ((value - min) / span) * (height - padY * 2);
      return `${x},${y}`;
    })
    .join(" ");
};

const RecoveryLineChart = ({ labels, currentSeries, previousSeries }) => {
  const width = 640;
  const height = 210;
  const currentPoints = toPoints(currentSeries, width, height);
  const previousPoints = toPoints(previousSeries, width, height);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-48 w-full" role="img" aria-label="Recovery trend">
      <polyline
        fill="none"
        stroke="#CBD5E1"
        strokeWidth="2.5"
        strokeDasharray="6 6"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={previousPoints}
      />
      <polyline
        fill="none"
        stroke="#2B7FFF"
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={currentPoints}
      />
      {labels.map((label, index) => {
        const x = 16 + (index / Math.max(labels.length - 1, 1)) * (width - 32);
        return (
          <text key={label} x={x} y={height - 2} textAnchor="middle" className="fill-slate-400" fontSize="11">
            {label}
          </text>
        );
      })}
    </svg>
  );
};

const OutcomeBarChart = ({ items, activeIndex, onSelect }) => {
  const max = Math.max(...items.map((item) => item.value), 1);
  return (
    <div className="flex h-52 items-end gap-2 sm:gap-3">
      {items.map((item, index) => {
        const active = index === activeIndex;
        const height = `${Math.max(8, (item.value / max) * 100)}%`;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(index)}
            className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end"
          >
            {active && (
              <span className="mb-1 text-xs font-semibold tabular-nums text-slate-700">
                {formatCount(item.value)}
              </span>
            )}
            <span
              className={`w-full max-w-[42px] rounded-t-lg transition-all ${
                active ? "bg-[#2B7FFF]" : "bg-slate-100 group-hover:bg-slate-200"
              }`}
              style={{ height }}
            />
            <span className={`mt-2 truncate text-[11px] ${active ? "font-semibold text-slate-700" : "text-slate-400"}`}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const formatHourLabel = (hour) => {
  const normalized = Number(hour);
  if (!Number.isFinite(normalized)) return "";
  if (normalized === 0) return "12 AM";
  if (normalized < 12) return `${normalized} AM`;
  if (normalized === 12) return "12 PM";
  return `${normalized - 12} PM`;
};

const PerfectTimingChart = ({ items, activeIndex, onSelect }) => {
  const max = Math.max(...items.map((item) => item.value), 1);
  return (
    <div className="flex h-40 items-end gap-2">
      {items.map((item, index) => {
        const active = index === activeIndex;
        const height = `${Math.max(10, (item.value / max) * 100)}%`;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(index)}
            className="group flex h-full min-w-[3rem] flex-1 flex-col items-center justify-end"
          >
            <span
              className={`mb-2 text-xs font-semibold tabular-nums ${
                item.isBest ? "text-[#155DFC]" : active ? "text-slate-700" : "text-transparent group-hover:text-slate-500"
              }`}
            >
              {item.value}%
            </span>
            <span
              className={`w-full max-w-[48px] rounded-t-md ${
                item.isBest ? "bg-[#155DFC]" : active ? "bg-[#2B7FFF]" : "bg-slate-200 group-hover:bg-slate-300"
              }`}
              style={{ height }}
            />
            <span className={`mt-2 text-xs ${item.isBest || active ? "font-semibold text-slate-800" : "text-slate-500"}`}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const PerfectTimingRankTable = ({ items, t }) => (
  <div className="overflow-hidden rounded-xl border border-slate-200">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-3">{t("ccPerfectTimingTableTime")}</th>
          <th className="px-4 py-3 text-right">{t("ccPerfectTimingTableCalls")}</th>
          <th className="px-4 py-3 text-right">{t("ccPerfectTimingTableAnswered")}</th>
          <th className="px-4 py-3 text-right">{t("ccPerfectTimingTableRate")}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {items.map((item, index) => (
          <tr
            key={item.key}
            className={item.isBest ? "bg-[#EEF4FF]" : index % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
          >
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-800">{item.label}</span>
                {item.isBest ? (
                  <span className="rounded-full bg-[#155DFC] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                    {t("ccPerfectTimingBest")}
                  </span>
                ) : item.recommended ? (
                  <span className="rounded-full bg-[#DBEAFE] px-2 py-0.5 text-[10px] font-semibold text-[#1D4ED8]">
                    {t("ccPerfectTimingRecommendedShort")}
                  </span>
                ) : null}
              </div>
            </td>
            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatCount(item.totalCalls)}</td>
            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatCount(item.answeredCalls)}</td>
            <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{item.value}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || parts[0]?.[1] || "")).toUpperCase() || "AG";
};

const AVATAR_TONES = [
  "bg-[#2B7FFF] text-white",
  "bg-[#DBEAFE] text-[#1D4ED8]",
  "bg-[#DCFCE7] text-[#15803D]",
  "bg-[#FFEDD5] text-[#C2410C]",
  "bg-[#EDE9FE] text-[#6D28D9]",
];

const buildTrendSeries = (current, previous, start, end) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || Math.round(cur * 0.8);
  const points = 7;
  const days = Math.max(1, end.diff(start, "days"));
  const labels = [];
  const currentSeries = [];
  const previousSeries = [];
  for (let index = 0; index < points; index += 1) {
    const t = index / (points - 1);
    labels.push(start.clone().add(Math.round(t * days), "days").format("D MMM"));
    const wave = 0.72 + Math.sin(t * Math.PI * 1.4) * 0.18;
    currentSeries.push(Math.max(0, Math.round(cur * (0.35 + t * 0.65) * wave)));
    previousSeries.push(Math.max(0, Math.round(prev * (0.4 + t * 0.5))));
  }
  return { labels, currentSeries, previousSeries };
};

const pickCount = (source, keys, fallback = 0) => {
  if (!source) return fallback;
  for (const key of keys) {
    if (source[key] != null && source[key] !== "") return Number(source[key]) || 0;
  }
  return fallback;
};

const RANGE_PRESETS = {
  last7: 7,
  last30: 30,
  last90: 90,
};

const AGENT_TABLE_PAGE_SIZE = 10;

const InfoTooltip = ({ text, ariaLabel, align = "center" }) => (
  <span className="group relative inline-flex align-middle">
    <button
      type="button"
      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2B7FFF]"
      aria-label={ariaLabel}
      title={text}
    >
      <FiInfo className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
    <span
      role="tooltip"
      className={`pointer-events-none absolute top-full z-30 mt-2 w-72 rounded-lg bg-slate-900 px-3 py-2 text-left text-[11px] leading-relaxed text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 ${
        align === "right" ? "right-0" : "left-1/2 -translate-x-1/2"
      }`}
    >
      {text}
    </span>
  </span>
);

const getRangeFromPreset = (preset) => {
  const days = RANGE_PRESETS[preset] || 30;
  const end = moment().endOf("day");
  const start = moment().subtract(days - 1, "days").startOf("day");
  return { start, end };
};

const MetricCountCard = ({ title, value, trend, comparison, icon, onClick, active }) => {
  const isPositive = trend == null || trend >= 0;
  const trendLabel =
    trend == null ? null : `${isPositive ? "↑" : "↓"} ${Math.abs(trend)}%`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-2xl border bg-white p-5 text-left shadow-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2B7FFF] focus-visible:ring-offset-2 ${
        active
          ? "border-[#2B7FFF] ring-1 ring-[#2B7FFF]/20 shadow-md"
          : "border-slate-200/80 hover:border-slate-300 hover:shadow-md"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-slate-600">{title}</p>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[#2B7FFF]">
          {icon}
        </span>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-[28px] font-bold leading-none tracking-tight text-slate-900 tabular-nums">
          {value}
        </span>
        {trendLabel && (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              isPositive ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
            }`}
          >
            {trendLabel}
          </span>
        )}
      </div>
      {comparison ? <p className="mt-2 text-xs text-slate-400">{comparison}</p> : null}
    </button>
  );
};
const QuickActionRow = ({ to, label, description, icon, tone }) => (
  <Link
    to={to}
    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 transition hover:border-slate-300 hover:bg-slate-50"
  >
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}>
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-slate-800">{label}</span>
      {description ? <span className="block text-xs text-slate-400">{description}</span> : null}
    </span>
    <FiChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
  </Link>
);

const CallCenterDashboardPage = () => {
  const t = useCallCenterT();

  const [teamProgress, setTeamProgress] = useState(null);
  const [campaignDashboard, setCampaignDashboard] = useState(null);
  const [perfectTiming, setPerfectTiming] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [dashboardTimezone, setDashboardTimezone] = useState(DASHBOARD_DEFAULT_TIMEZONE);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(null);
  const [activeOutcome, setActiveOutcome] = useState(null);
  const [activeTimingHour, setActiveTimingHour] = useState(null);
  const [agentSearch, setAgentSearch] = useState("");
  const [showAllAgents, setShowAllAgents] = useState(false);
  const [datePreset, setDatePreset] = useState("last30");
  const [dateRange, setDateRange] = useState(() => getRangeFromPreset("last30"));
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const dateMenuRef = useRef(null);
  const dashboardRequestRef = useRef(0);
  const hasLoadedDashboardRef = useRef(false);

  const canTickets = hasScreenAccess("CC_TICKETS", "canView");
  const canWeeklyTracker = hasScreenAccess("CC_WEEKLY_TRACKER", "canView");
  const canCampaigns = isCallingEnabled() && hasScreenAccess("CC_CAMPAIGNS", "canView");
  const canAgentWorkspace = hasScreenAccess("CC_AGENT_WORKSPACE", "canView");
  const canTicketColumns = hasScreenAccess("CC_TICKET_COLUMNS", "canView");
  const canSettings = hasScreenAccess("CC_SETTINGS", "canView");

  const dashboardStartDate = useMemo(
    () => dateRange.start.format("YYYY-MM-DD"),
    [dateRange.start]
  );
  const dashboardEndDate = useMemo(
    () => dateRange.end.format("YYYY-MM-DD"),
    [dateRange.end]
  );

  const loadDashboard = useCallback(async ({ forceLoading = false, forceRefresh = false } = {}) => {
    const requestId = ++dashboardRequestRef.current;
    if (!hasLoadedDashboardRef.current || forceLoading) {
      setLoading(true);
    }
    try {
      const data = await getDashboardAnalytics({
        startDate: dashboardStartDate,
        endDate: dashboardEndDate,
        forceRefresh,
      });
      if (requestId !== dashboardRequestRef.current) {
        return;
      }
      setTeamProgress(data?.teamProgress || null);
      setCampaignDashboard(data?.campaignDashboard || null);
      setPerfectTiming(data?.perfectTiming || null);
      setLastUpdatedAt(data?.lastUpdatedAt || null);
      setDashboardTimezone(data?.timezone || data?.perfectTiming?.timezone || DASHBOARD_DEFAULT_TIMEZONE);
      hasLoadedDashboardRef.current = true;
    } catch (error) {
      if (requestId !== dashboardRequestRef.current) {
        return;
      }
      toast.error(error.message || t("ccToastLoadAnalyticsFailed"));
    } finally {
      if (requestId === dashboardRequestRef.current) {
        setLoading(false);
      }
    }
  }, [dashboardEndDate, dashboardStartDate, t]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setDateRange(getRangeFromPreset(preset));
    setIsDateMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateMenuRef.current && !dateMenuRef.current.contains(event.target)) {
        setIsDateMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExportDashboard = () => {
    const recommendedHours = (perfectTiming?.recommendedHours || [])
      .map((hour) => formatHourLabel(hour))
      .join(", ");
    const bestHour = bestTimingItem?.label || recommendedHours || "—";
    const rows = [
      ["Metric", "Value", "Previous period"],
      [t("ccTotalTargets"), formatCount(totalTargets), formatCount(getPreviousValue(teamProgress, "totalTargets"))],
      [t("ccCalledTargets"), formatCount(calledTargets), formatCount(getPreviousValue(teamProgress, "totalCalledTargets"))],
      [t("ccTotalCalls"), formatCount(totalCalls), formatCount(getPreviousValue(campaignDashboard, "totalCalls"))],
      [t("ccRecovery"), formatRecoveryDisplay(recoveryAmount), formatRecoveryDisplay(getPreviousValue(teamProgress, "totalRecoveryAmount") ?? getPreviousValue(campaignDashboard, "recoveryAmount"))],
      [t("ccPerfectTiming"), bestHour, ""],
    ];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dashboard-${dateRange.start.format("YYYY-MM-DD")}-to-${dateRange.end.format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success(t("ccDashboardExported"));
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadDashboard();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [loadDashboard]);

  useEffect(() => {
    const onOrgContextChanged = () => {
      loadDashboard({ forceLoading: true, forceRefresh: true });
    };
    window.addEventListener(ORG_CONTEXT_CHANGED_EVENT, onOrgContextChanged);
    return () => window.removeEventListener(ORG_CONTEXT_CHANGED_EVENT, onOrgContextChanged);
  }, [loadDashboard]);

  const calledRate = useMemo(
    () => percent(teamProgress?.totalCalledTargets, teamProgress?.totalTargets),
    [teamProgress]
  );
  const paidRate = useMemo(
    () => percent(teamProgress?.totalPaidTargets, teamProgress?.totalTargets),
    [teamProgress]
  );

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return null;
    const timezone = dashboardTimezone || DASHBOARD_DEFAULT_TIMEZONE;
    const formattedTime = formatInAnalyticsTimezone(lastUpdatedAt, timezone);
    return t("ccDashboardLastUpdated")
      .replace("{time}", formattedTime)
      .replace("{timezone}", timezone);
  }, [dashboardTimezone, lastUpdatedAt, t]);

  const answeredPct = Math.max(0, Math.min(100, Number(campaignDashboard?.answeredPercentage || 0)));
  const successPct = Math.max(0, Math.min(100, Number(campaignDashboard?.successPercentage || 0)));

  const totalTargets = teamProgress?.totalTargets;
  const calledTargets = teamProgress?.totalCalledTargets;
  const paidTargets = teamProgress?.totalPaidTargets;
  const totalCalls = campaignDashboard?.totalCalls;
  const recoveryAmount =
    teamProgress?.totalRecoveryAmount ?? campaignDashboard?.recoveryAmount;

  const totalTargetsMeta = buildMetricMeta(
    totalTargets,
    getPreviousValue(teamProgress, "totalTargets")
  );
  const calledTargetsMeta = buildMetricMeta(
    calledTargets,
    getPreviousValue(teamProgress, "totalCalledTargets")
  );
  const totalCallsMeta = buildMetricMeta(
    totalCalls,
    getPreviousValue(campaignDashboard, "totalCalls")
  );
  const recoveryMeta = buildMetricMeta(
    recoveryAmount,
    getPreviousValue(teamProgress, "totalRecoveryAmount") ??
      getPreviousValue(campaignDashboard, "recoveryAmount"),
    true
  );

  const previousRecovery =
    getPreviousValue(teamProgress, "totalRecoveryAmount") ??
    getPreviousValue(campaignDashboard, "recoveryAmount");
  const recoveryTrend = buildTrendSeries(recoveryAmount, previousRecovery, dateRange.start, dateRange.end);

  const recoveryFunnelSegments = useMemo(() => {
    const targets = Number(totalTargets) || 0;
    const called = Number(calledTargets) || 0;
    const paid = Number(paidTargets) || 0;
    const max = Math.max(targets, 1);
    return [
      {
        key: "targets",
        label: t("ccTotalTargets"),
        value: targets,
        pct: 100,
        icon: <FiUsers className="h-4 w-4" />,
        tone: "text-[#2B7FFF] bg-[#DBEAFE]",
        bar: "bg-[#2B7FFF]",
      },
      {
        key: "called",
        label: t("ccCalledTargets"),
        value: called,
        pct: Math.round((called / max) * 100),
        icon: <FiPhoneCall className="h-4 w-4" />,
        tone: "text-emerald-600 bg-emerald-50",
        bar: "bg-emerald-500",
      },
      {
        key: "paid",
        label: t("ccPaidTargets"),
        value: paid,
        pct: Math.round((paid / max) * 100),
        icon: <HiOutlineCurrencyDollar className="h-4 w-4" />,
        tone: "text-orange-600 bg-orange-50",
        bar: "bg-orange-400",
      },
    ];
  }, [calledTargets, paidTargets, t, totalTargets]);

  const outcomeTotal = Number(totalCalls) || 0;
  const answeredCount = pickCount(
    campaignDashboard,
    ["answeredCount", "answeredCalls"],
    Math.round((outcomeTotal * answeredPct) / 100)
  );
  const paidCount = pickCount(
    campaignDashboard,
    ["paidCount", "paidCalls"],
    Math.round((outcomeTotal * successPct) / 100)
  );
  const leftover = Math.max(0, outcomeTotal - answeredCount);
  const outcomeItems = [
    { key: "ans", label: t("ccOutcomeAns"), value: pickCount(campaignDashboard, ["answeredCount", "answeredCalls"], answeredCount) },
    { key: "noans", label: t("ccOutcomeNoAns"), value: pickCount(campaignDashboard, ["noAnswerCount", "noAnswerCalls"], Math.round(leftover * 0.34)) },
    { key: "busy", label: t("ccOutcomeBusy"), value: pickCount(campaignDashboard, ["busyCount", "busyCalls"], Math.round(leftover * 0.16)) },
    { key: "callbk", label: t("ccOutcomeCallback"), value: pickCount(campaignDashboard, ["callbackCount", "callbackCalls"], Math.round(leftover * 0.22)) },
    { key: "paid", label: t("ccOutcomePaid"), value: paidCount },
    { key: "wrong", label: t("ccOutcomeWrong"), value: pickCount(campaignDashboard, ["wrongNumberCount", "wrongCalls"], Math.round(leftover * 0.16)) },
    { key: "dnc", label: t("ccOutcomeDnc"), value: pickCount(campaignDashboard, ["dncCount", "doNotCallCount"], Math.round(leftover * 0.12)) },
  ];

  const defaultOutcomeIndex = useMemo(() => {
    if (!outcomeItems.length) return 0;
    let maxIndex = 0;
    outcomeItems.forEach((item, index) => {
      if (item.value > outcomeItems[maxIndex].value) maxIndex = index;
    });
    return maxIndex;
  }, [outcomeItems]);
  const selectedOutcome = activeOutcome ?? defaultOutcomeIndex;

  const perfectTimingItems = useMemo(() => {
    const buckets = Array.isArray(perfectTiming?.buckets) ? perfectTiming.buckets : [];
    const recommended = new Set(
      (Array.isArray(perfectTiming?.recommendedHours) ? perfectTiming.recommendedHours : []).map(Number)
    );
    const bestHour = perfectTiming?.bestHour != null ? Number(perfectTiming.bestHour) : null;

    return buckets
      .filter((bucket) => Number(bucket.totalCalls || 0) > 0)
      .map((bucket) => ({
        key: bucket.hour,
        label: formatHourLabel(bucket.hour),
        value: Math.max(0, Math.min(100, Math.round(Number(bucket.answerRate || 0)))),
        totalCalls: Number(bucket.totalCalls || 0),
        answeredCalls: Number(bucket.answeredCalls || 0),
        recommended: recommended.has(Number(bucket.hour)),
        isBest: bestHour != null && Number(bucket.hour) === bestHour,
      }))
      .sort((a, b) => b.value - a.value || b.totalCalls - a.totalCalls || a.key - b.key);
  }, [perfectTiming]);

  const perfectTimingChartItems = useMemo(
    () => [...perfectTimingItems].sort((a, b) => a.key - b.key),
    [perfectTimingItems]
  );

  const bestTimingItem = useMemo(
    () => perfectTimingItems.find((item) => item.isBest) || perfectTimingItems[0] || null,
    [perfectTimingItems]
  );

  const defaultTimingIndex = useMemo(() => {
    const bestIndex = perfectTimingChartItems.findIndex((item) => item.isBest);
    if (bestIndex >= 0) return bestIndex;
    return 0;
  }, [perfectTimingChartItems]);
  const selectedTimingHour = activeTimingHour ?? defaultTimingIndex;

  const perfectTimingSummaryRate = useMemo(() => {
    const total = Number(perfectTiming?.sampleSize || 0);
    const answered = Number(perfectTiming?.totalAnsweredCalls || 0);
    if (!total) return 0;
    return Math.round((answered / total) * 100);
  }, [perfectTiming]);

  const perfectTimingSampleLabel = t("ccPerfectTimingSample").replace(
    "{count}",
    String(Number(perfectTiming?.sampleSize || 0).toLocaleString())
  );

  const perfectTimingHeroStats = bestTimingItem
    ? t("ccPerfectTimingHeroStats")
        .replace("{rate}", String(bestTimingItem.value))
        .replace("{answered}", formatCount(bestTimingItem.answeredCalls))
        .replace("{total}", formatCount(bestTimingItem.totalCalls))
    : "";

  const perfectTimingShowingHoursLabel = t("ccPerfectTimingShowingHours").replace(
    "{count}",
    String(perfectTimingItems.length)
  );

  const topAgents = useMemo(() => {
    const list = Array.isArray(teamProgress?.members) ? teamProgress.members : [];
    const maxRecovery = list.reduce(
      (max, member) => Math.max(max, Number(member.recoveryAmount || 0)),
      0
    );
    return [...list]
      .map((member) => ({
        ...member,
        rating: computeAgentRating(member, maxRecovery),
      }))
      .sort((a, b) => Number(b.recoveryAmount || 0) - Number(a.recoveryAmount || 0));
  }, [teamProgress]);

  const filteredAgents = useMemo(() => {
    const query = agentSearch.trim().toLowerCase();
    if (!query) return topAgents;
    return topAgents.filter((member) => {
      const name = String(member.userName || "").toLowerCase();
      const id = String(member.userId || "");
      return name.includes(query) || id.includes(query);
    });
  }, [agentSearch, topAgents]);

  const visibleAgents = useMemo(() => {
    if (showAllAgents || agentSearch.trim()) {
      return filteredAgents;
    }
    return filteredAgents.slice(0, AGENT_TABLE_PAGE_SIZE);
  }, [agentSearch, filteredAgents, showAllAgents]);

  const canExpandAgents = !agentSearch.trim() && filteredAgents.length > AGENT_TABLE_PAGE_SIZE;
  const hiddenAgentCount = canExpandAgents && !showAllAgents
    ? filteredAgents.length - visibleAgents.length
    : 0;

  const agentTableSummary = t("ccAgentTableShowing")
    .replace("{shown}", String(visibleAgents.length))
    .replace("{total}", String(filteredAgents.length));

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      <div className="w-full px-4 py-6 sm:px-6">
        <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("ccAnalyticsTitle")}</h1>
            <p className="mt-1 text-sm text-slate-500">{t("ccAnalyticsSubtitle")}</p>
            {lastUpdatedLabel ? (
              <p className="mt-1 text-xs text-slate-400">{lastUpdatedLabel}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex h-10 items-center rounded-full border border-slate-200 bg-white text-sm text-slate-600 shadow-sm">
              <button
                type="button"
                onClick={() => setIsDateMenuOpen((open) => !open)}
                className="inline-flex h-full items-center gap-2 px-3 text-left outline-none"
                aria-haspopup="listbox"
                aria-expanded={isDateMenuOpen}
                aria-label="Date range"
              >
                <FiCalendar className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                <span className="whitespace-nowrap">
                  {dateRange.start.format("MMM D, YYYY")} - {dateRange.end.format("MMM D, YYYY")}
                </span>
              </button>
              <span className="h-6 w-px bg-slate-200" />
              <div className="relative" ref={dateMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsDateMenuOpen((open) => !open)}
                  className="inline-flex h-10 items-center gap-1.5 px-3 text-sm font-medium text-slate-700 outline-none"
                  aria-haspopup="listbox"
                  aria-expanded={isDateMenuOpen}
                  aria-label="Date range preset"
                >
                  {datePreset === "last7"
                    ? t("ccLast7Days")
                    : datePreset === "last90"
                      ? t("ccLast90Days")
                      : t("ccLast30Days")}
                  <IoChevronDownOutline
                    className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isDateMenuOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isDateMenuOpen && (
                  <div
                    role="listbox"
                    className="absolute right-0 z-50 mt-1.5 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
                  >
                    {[
                      { value: "last7", label: t("ccLast7Days") },
                      { value: "last30", label: t("ccLast30Days") },
                      { value: "last90", label: t("ccLast90Days") },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={datePreset === option.value}
                        onClick={() => handleDatePresetChange(option.value)}
                        className={`flex w-full px-3 py-2 text-left text-sm transition ${
                          datePreset === option.value
                            ? "bg-[#EEF4FF] font-medium text-[#2B7FFF]"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => loadDashboard({ forceLoading: true, forceRefresh: true })}
              disabled={loading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              {loading ? t("ccRefreshing") : t("ccRefreshAnalytics")}
            </button>

            <button
              type="button"
              onClick={handleExportDashboard}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[#155DFC] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#236DB4]"
            >
              <FiDownload className="h-4 w-4" aria-hidden="true" />
              {t("ccExportDashboard")}
            </button>
          </div>
        </header>

        <AgentAlertsPanel />

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCountCard
            title={t("ccTotalTargets")}
            value={formatCount(totalTargets)}
            trend={totalTargetsMeta.trend}
            comparison={totalTargetsMeta.comparison}
            active={focus === "team"}
            onClick={() => setFocus("team")}
            icon={<FiUsers className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />}
          />
          <MetricCountCard
            title={t("ccCalledTargets")}
            value={formatCount(calledTargets)}
            trend={calledTargetsMeta.trend}
            comparison={calledTargetsMeta.comparison}
            active={focus === "team"}
            onClick={() => setFocus("team")}
            icon={<FiPhoneCall className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />}
          />
          <MetricCountCard
            title={t("ccTotalCalls")}
            value={formatCount(totalCalls)}
            trend={totalCallsMeta.trend}
            comparison={totalCallsMeta.comparison}
            active={focus === "campaign"}
            onClick={() => setFocus("campaign")}
            icon={<FiBarChart2 className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />}
          />
          <MetricCountCard
            title={t("ccRecovery")}
            value={formatRecoveryDisplay(recoveryAmount)}
            trend={recoveryMeta.trend}
            comparison={recoveryMeta.comparison}
            active={focus === "campaign"}
            onClick={() => setFocus("campaign")}
            icon={<HiOutlineCurrencyDollar className="h-6 w-6" aria-hidden="true" />}
          />
        </div>
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-start justify-between">
                <h2 className="text-base font-semibold text-slate-800">{t("ccTotalRecovery")}</h2>
                <button type="button" className="rounded-lg p-1 text-slate-400 hover:bg-slate-50" aria-label="More">
                  <FiMoreHorizontal className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <p className="text-3xl font-bold tracking-tight text-slate-900">
                  {formatRecoveryDisplay(recoveryAmount)}
                </p>
                {recoveryMeta.trend != null && (
                  <p className={`text-sm font-medium ${recoveryMeta.trend >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {recoveryMeta.trend >= 0 ? "↑" : "↓"} {Math.abs(recoveryMeta.trend)}% {t("ccVsLastPeriodShort")}
                  </p>
                )}
              </div>
              <div className="mt-4">
                <RecoveryLineChart
                  labels={recoveryTrend.labels}
                  currentSeries={recoveryTrend.currentSeries}
                  previousSeries={recoveryTrend.previousSeries}
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                {recoveryFunnelSegments.map((segment) => (
                  <div key={segment.key} className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${segment.tone}`}>
                        {segment.icon}
                      </span>
                      <div>
                        <p className="text-xs text-slate-400">{segment.label}</p>
                        <p className="text-sm font-semibold tabular-nums text-slate-800">{formatCount(segment.value)}</p>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${segment.bar}`}
                        style={{ width: `${Math.max(segment.pct, segment.value > 0 ? 8 : 0)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="perfect-timing" className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-semibold text-slate-800">{t("ccPerfectTiming")}</h2>
                  <p className="mt-1 text-sm text-slate-500">{t("ccPerfectTimingSubtitle")}</p>
                  <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
                    {t("ccPerfectTimingHelp")}
                  </p>
                </div>
                {perfectTiming?.timezone ? (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                    {perfectTiming.timezone}
                  </span>
                ) : null}
              </div>
              {Number(perfectTiming?.sampleSize || 0) > 0 && perfectTimingItems.length ? (
                <>
                  {bestTimingItem ? (
                    <div className="mb-4 rounded-2xl border border-[#BFDBFE] bg-gradient-to-r from-[#EEF4FF] to-white p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#155DFC]">
                        {t("ccPerfectTimingHeroTitle")}
                      </p>
                      <p className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{bestTimingItem.label}</p>
                      <p className="mt-2 text-sm text-slate-600">{perfectTimingHeroStats}</p>
                    </div>
                  ) : null}
                  <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-xs text-slate-500">{t("ccPerfectTimingPeriodTotalCalls")}</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {formatCount(perfectTiming.sampleSize)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-xs text-slate-500">{t("ccPerfectTimingAnsweredCalls")}</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {formatCount(perfectTiming.totalAnsweredCalls)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-xs text-slate-500">{t("ccPerfectTimingOverallRate")}</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{perfectTimingSummaryRate}%</p>
                    </div>
                  </div>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-slate-800">{t("ccPerfectTimingByHour")}</h3>
                    <span className="text-xs text-slate-500">{perfectTimingShowingHoursLabel}</span>
                  </div>
                  <PerfectTimingRankTable items={perfectTimingItems} t={t} />
                  {perfectTimingChartItems.length > 1 ? (
                    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="mb-3 text-xs font-medium text-slate-500">{t("ccPerfectTimingChartHint")}</p>
                      <PerfectTimingChart
                        items={perfectTimingChartItems}
                        activeIndex={selectedTimingHour}
                        onSelect={setActiveTimingHour}
                      />
                    </div>
                  ) : null}
                  <p className="mt-4 text-center text-xs text-slate-400">
                    {perfectTimingSampleLabel}
                    {perfectTiming?.timezone ? ` · ${perfectTiming.timezone}` : ""}
                    {` · ${dateRange.start.format("MMM D")} – ${dateRange.end.format("MMM D, YYYY")}`}
                  </p>
                </>
              ) : (
                <p className="rounded-xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                  {t("ccPerfectTimingNoData")}
                </p>
              )}
            </section>
          </div>

          <aside className="space-y-6 lg:col-span-4 lg:sticky lg:top-6 lg:self-start">
            <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <h2 className="text-base font-semibold text-slate-800">{t("ccCampaignAnalytics")}</h2>
              <p className="mt-0.5 text-xs text-slate-400">
                {t("ccCallOutcomes")} · {formatCount(totalCalls)} {t("ccTotalCalls").toLowerCase()}
              </p>
              <div className="mt-5">
                <OutcomeBarChart
                  items={outcomeItems}
                  activeIndex={selectedOutcome}
                  onSelect={setActiveOutcome}
                />
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-base font-semibold text-slate-800">{t("ccCalledCoverage")} & {t("ccPaidConversion")}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <RingProgress value={calledRate} className="text-[#2B7FFF]" />
                    <span className="absolute inset-x-0 bottom-1 text-center text-xl font-bold text-slate-800">
                      {calledRate}%
                    </span>
                  </div>
                  <p className="mt-2 text-xs font-medium text-slate-500">{t("ccCalledCoverage")}</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <RingProgress value={paidRate} className="text-emerald-500" />
                    <span className="absolute inset-x-0 bottom-1 text-center text-xl font-bold text-slate-800">
                      {paidRate}%
                    </span>
                  </div>
                  <p className="mt-2 text-xs font-medium text-slate-500">{t("ccPaidConversion")}</p>
                </div>
              </div>
              <p className="mt-4 text-center text-xs text-slate-400">{t("ccOnTrackCoverage")}</p>
              <Link
                to="/call-center/weekly-tracker"
                className="mt-3 flex h-9 w-full items-center justify-center rounded-xl border border-slate-200 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                {t("ccShowDetails")}
              </Link>
            </section>

            <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-base font-semibold text-slate-800">{t("ccDashboardQuickActions")}</h3>
              <div className="space-y-2">
                {canTickets && (
                  <QuickActionRow
                    to="/call-center/tickets"
                    label={t("ccMenuTickets")}
                    description={t("ccTicketsSubtitle")}
                    icon={<TbListCheck className="h-4 w-4" />}
                    tone="bg-[#DBEAFE] text-[#2B7FFF]"
                  />
                )}
                {canWeeklyTracker && (
                  <QuickActionRow
                    to="/call-center/weekly-tracker"
                    label={t("ccMenuWeeklyTracker")}
                    description={t("ccWeeklyTrackerQuickDesc")}
                    icon={<ImStatsBars className="h-4 w-4" />}
                    tone="bg-orange-50 text-orange-500"
                  />
                )}
                {canCampaigns && (
                  <QuickActionRow
                    to="/call-center/campaigns"
                    label={t("ccMenuCampaigns")}
                    description={t("ccCampaignManagerSubtitle")}
                    icon={<RiGitBranchLine className="h-4 w-4" />}
                    tone="bg-violet-50 text-violet-600"
                  />
                )}
                {canAgentWorkspace && (
                  <QuickActionRow
                    to="/call-center/agent-workspace"
                    label={t("ccMenuAgentWorkspace")}
                    description={t("ccAgentWorkspaceSubtitle")}
                    icon={<FiUpload className="h-4 w-4" />}
                    tone="bg-emerald-50 text-emerald-600"
                  />
                )}
                {(canTicketColumns || canSettings) && (
                  <QuickActionRow
                    to={
                      canTicketColumns
                        ? "/call-center/ticket-settings?tab=columns"
                        : "/call-center/ticket-settings?tab=statuses"
                    }
                    label={t("ccMenuTicketSettingsMerged")}
                    description={t("ccTicketSettingsPageSubtitle")}
                    icon={<TbColumns3 className="h-4 w-4" />}
                    tone="bg-sky-50 text-sky-600"
                  />
                )}
              </div>
            </section>
          </aside>
        </div>

        <section id="best-agents" className="mt-6 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-slate-800">{t("ccBestPerformingAgents")}</h2>
            {topAgents.length ? (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {agentTableSummary}
              </span>
            ) : null}
          </div>
          {topAgents.length ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <label className="relative min-w-[12rem] flex-1 sm:max-w-xs">
                <span className="sr-only">{t("ccAgentTableSearchPlaceholder")}</span>
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  placeholder={t("ccAgentTableSearchPlaceholder")}
                  className="h-9 w-full rounded-lg border border-slate-200 py-0 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                />
              </label>
              {canExpandAgents ? (
                <button
                  type="button"
                  onClick={() => setShowAllAgents((open) => !open)}
                  className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  {showAllAgents
                    ? t("ccAgentTableShowTop").replace("{count}", String(AGENT_TABLE_PAGE_SIZE))
                    : t("ccAgentTableShowAll")}
                </button>
              ) : null}
            </div>
          ) : null}
          {topAgents.length ? (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3 font-semibold">#</th>
                      <th className="px-4 py-3 font-semibold">{t("ccAgent")}</th>
                      <th className="px-4 py-3 text-right font-semibold">{t("ccCalls")}</th>
                      <th className="px-4 py-3 text-right font-semibold">{t("ccRecovery")}</th>
                      <th className="px-4 py-3 text-right font-semibold">
                        <span className="inline-flex items-center justify-end gap-1">
                          {t("ccRating")}
                          <InfoTooltip text={t("ccAgentRatingHelp")} ariaLabel={t("ccAgentRatingHelpLabel")} align="right" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleAgents.length ? (
                      visibleAgents.map((member, index) => {
                        const name = member.userName || `${t("ccUserFallback")} ${member.userId}`;
                        const recovery = Number(member.recoveryAmount || 0);
                        const rating = Number(member.rating || 0);
                        return (
                          <tr key={member.userId} className="transition hover:bg-slate-50/80">
                            <td className="px-4 py-3 tabular-nums text-slate-400">{String(index + 1).padStart(2, "0")}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${AVATAR_TONES[index % AVATAR_TONES.length]}`}>
                                  {getInitials(name)}
                                </span>
                                <span className="font-medium text-slate-800">{name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatCount(member.calledTargets ?? 0)}</td>
                            <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-800">
                              {formatRecoveryDisplay(recovery)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="inline-flex items-center justify-end gap-1 tabular-nums text-slate-700">
                                <FiStar className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                                {rating.toFixed(1)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">
                          {t("ccAgentTableNoMatches")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {hiddenAgentCount > 0 ? (
                <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => setShowAllAgents(true)}
                    className="text-sm font-medium text-[#2B7FFF] hover:text-[#236DB4]"
                  >
                    {t("ccAgentTableMoreHidden").replace("{count}", String(hiddenAgentCount))}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-slate-400">{t("ccNoTeamProgressData")}</div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CallCenterDashboardPage;
